/**
 * Rebuy Promotional Pricing Handler
 * ------------------------------------------------------------------------------
 * Modifies Rebuy widget prices to show promotional pricing
 * Since Rebuy renders their own DOM, we need to hook into their lifecycle
 * v1.0.4 - FOXH-166: Uses IntersectionObserver for reliable visibility detection
 *          instead of getBoundingClientRect() which has timing/race condition issues
 */

class RebuyPromoPricing {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Wait for Rebuy to load and render
    if (typeof Rebuy !== 'undefined') {
      this.applyPromoPricing();
      this.patchFlickityButtons();
      this.reorderNavButtonsForFocusOrder();
      this.removePriceTabindex();
      this.handleHiddenSlides();
      this.initFlickityDragFix();
    } else {
      // Wait for Rebuy to load
      document.addEventListener('rebuy:ready', () => {
        this.applyPromoPricing();
        this.patchFlickityButtons();
        this.reorderNavButtonsForFocusOrder();
        this.removePriceTabindex();
        this.handleHiddenSlides();
        this.initFlickityDragFix();
      });
    }

    // Also listen for product additions/updates
    document.addEventListener('rebuy:products.loaded', () => {
      this.applyPromoPricing();
      this.patchFlickityButtons();
      this.reorderNavButtonsForFocusOrder();
      this.removePriceTabindex();
      this.handleHiddenSlides();
      this.initFlickityDragFix();
    });

    // Flickity may initialize after Rebuy products load - run after delay
    // This ensures we catch carousels that initialize asynchronously
    setTimeout(() => {
      this.reorderNavButtonsForFocusOrder();
      this.handleHiddenSlides();
      this.initFlickityDragFix();
    }, 500);

    setTimeout(() => {
      this.reorderNavButtonsForFocusOrder();
      this.handleHiddenSlides();
      this.initFlickityDragFix();
    }, 1500);

    // Use MutationObserver to catch when Rebuy adds new products
    this.setupMutationObserver();
  }

  setupMutationObserver() {
    // Watch for Rebuy widgets being added to the DOM
    const targetNode = document.body;
    const config = {
      childList: true,
      subtree: true,
    };

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if this is a Rebuy element or Flickity button
            if (
              node.classList?.contains('rebuy-product-block') ||
              node.querySelector?.('.rebuy-product-block') ||
              node.classList?.contains('flickity-button') ||
              node.querySelector?.('.flickity-button') ||
              node.classList?.contains('flickity-enabled') ||
              node.querySelector?.('.flickity-enabled')
            ) {
              // Small delay to ensure Rebuy has finished rendering
              setTimeout(() => {
                this.applyPromoPricing();
                this.patchFlickityButtons();
                this.reorderNavButtonsForFocusOrder();
                this.removePriceTabindex();
                // Force re-observe when new slides are added (Rebuy re-render)
                this.handleHiddenSlides(true);
                // FOXH-142: Apply drag fix to newly added carousels
                this.initFlickityDragFix();
              }, 100);
            }
          }
        });
      });
    });

    this.observer.observe(targetNode, config);
  }

  /**
   * Wait for Flickity to be fully initialized on a carousel
   * Uses requestAnimationFrame polling to avoid blocking
   * @param {HTMLElement} carousel - The Flickity carousel element
   * @param {number} maxAttempts - Maximum poll attempts (default 20, ~333ms)
   * @returns {Promise<Flickity|null>} The Flickity instance or null
   */
  waitForFlickity(carousel, maxAttempts = 20) {
    return new Promise((resolve) => {
      let attempts = 0;

      const checkFlickity = () => {
        attempts++;

        // Try multiple ways to get the Flickity instance
        const instance =
          window.Flickity?.data(carousel) ||
          carousel.flickityInstance ||
          (typeof jQuery !== 'undefined' &&
            jQuery?.(carousel)?.data?.('flickity'));

        // Verify instance is fully initialized with cells
        if (instance && instance.cells && instance.cells.length > 0) {
          resolve(instance);
          return;
        }

        if (attempts < maxAttempts) {
          requestAnimationFrame(checkFlickity);
        } else {
          // Flickity not found after max attempts, resolve with null
          resolve(null);
        }
      };

      checkFlickity();
    });
  }

  /**
   * Set up IntersectionObserver for reliable visibility detection
   * This avoids getBoundingClientRect() timing issues where elements
   * return 0 before layout is complete
   * @param {HTMLElement} carousel - The Flickity carousel element
   */
  setupVisibilityObserver(carousel) {
    const viewport = carousel.querySelector('.flickity-viewport');
    if (!viewport) return;

    // Clean up existing observer if present
    if (carousel._slideVisibilityObserver) {
      carousel._slideVisibilityObserver.disconnect();
    }

    // Track which slides are currently visible
    const visibleSlides = new Set();
    const context = this;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;
          // Consider slide visible if at least 10% is showing
          if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
            visibleSlides.add(slide);
          } else {
            visibleSlides.delete(slide);
          }
        });

        // Update accessibility for all slides based on visibility
        context.updateSlideAccessibilityWithVisibleSet(carousel, visibleSlides);
      },
      {
        root: viewport,
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: '0px',
      }
    );

    // Observe all slides
    const slides = carousel.querySelectorAll(
      '.flickity-slider > .rebuy-product-block, .flickity-slider > .flickity-cell, .flickity-slider > *'
    );
    slides.forEach((slide) => {
      // Skip non-slide elements
      if (slide.classList.contains('flickity-viewport')) return;
      observer.observe(slide);
    });

    // Store references for cleanup and state tracking
    carousel._slideVisibilityObserver = observer;
    carousel._visibleSlides = visibleSlides;
  }

  /**
   * Update slide accessibility using the visibility set from IntersectionObserver
   * @param {HTMLElement} carousel - The Flickity carousel element
   * @param {Set<HTMLElement>} visibleSlides - Set of currently visible slides
   */
  updateSlideAccessibilityWithVisibleSet(carousel, visibleSlides) {
    const focusableSelectors =
      'a[href], button:not(.flickity-button), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const slides = carousel.querySelectorAll(
      '.flickity-slider > .rebuy-product-block, .flickity-slider > .flickity-cell, .flickity-slider > *'
    );

    slides.forEach((slide) => {
      // Skip non-slide elements
      if (slide.classList.contains('flickity-viewport')) return;

      const isVisible = visibleSlides.has(slide);
      const focusableElements = slide.querySelectorAll(focusableSelectors);

      if (isVisible) {
        // Visible slide - restore accessibility
        slide.removeAttribute('aria-hidden');

        focusableElements.forEach((element) => {
          // Skip image links - they should stay non-focusable to reduce tab stops
          if (element.classList.contains('rebuy-product-image')) {
            element.setAttribute('tabindex', '-1');
            return;
          }

          // Restore original tabindex if we saved one
          if (element.dataset.a11yOrigTabindex !== undefined) {
            if (element.dataset.a11yOrigTabindex === 'none') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', element.dataset.a11yOrigTabindex);
            }
          } else if (element.getAttribute('tabindex') === '-1') {
            element.removeAttribute('tabindex');
          }
          element.removeAttribute('aria-hidden');
        });
      } else {
        // Hidden slide - prevent keyboard access
        slide.setAttribute('aria-hidden', 'true');

        focusableElements.forEach((element) => {
          // Save original tabindex before overwriting (only once)
          if (element.dataset.a11yOrigTabindex === undefined) {
            element.dataset.a11yOrigTabindex = element.hasAttribute('tabindex')
              ? element.getAttribute('tabindex')
              : 'none';
          }
          element.setAttribute('tabindex', '-1');
        });
      }
    });
  }

  /**
   * Patch Flickity prev/next buttons for keyboard accessibility
   * Ensures buttons are focusable and respond to Enter/Space
   */
  patchFlickityButtons() {
    const flickityButtons = document.querySelectorAll('.flickity-button');

    flickityButtons.forEach((button) => {
      // Skip if already patched
      if (button.dataset.a11yPatched === 'true') return;

      // Ensure button is focusable
      if (!button.hasAttribute('tabindex')) {
        button.setAttribute('tabindex', '0');
      }

      // Ensure it has type="button"
      if (!button.hasAttribute('type')) {
        button.setAttribute('type', 'button');
      }

      // Add aria-label if missing
      if (!button.hasAttribute('aria-label')) {
        if (button.classList.contains('previous')) {
          button.setAttribute('aria-label', 'Previous slide');
        } else if (button.classList.contains('next')) {
          button.setAttribute('aria-label', 'Next slide');
        }
      }

      // Add keyboard handler for Enter/Space
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          button.click();
        }
      });

      // Mark as patched
      button.dataset.a11yPatched = 'true';
    });
  }

  /**
   * FOXH-142: Fix Flickity carousel drag vs click sensitivity
   * Prevents accidental clicks when user is trying to drag/swipe the carousel
   * This addresses the A11y issue where swiping triggers click events
   */
  initFlickityDragFix() {
    const flickityCarousels = document.querySelectorAll('.flickity-enabled');

    flickityCarousels.forEach((flickityEl) => {
      // Skip if already patched
      if (flickityEl.dataset.dragFixPatched === 'true') return;

      let isDragging = false;
      let isPointerDown = false;
      let dragStartX = 0;
      let dragStartY = 0;
      const DRAG_THRESHOLD = 10; // Pixels of movement before considered a drag

      const resetDragState = () => {
        // Delay reset to allow click event to be intercepted
        setTimeout(() => {
          isDragging = false;
          isPointerDown = false;
          dragStartX = 0;
          dragStartY = 0;
        }, 100);
      };

      // Track pointer down position
      flickityEl.addEventListener(
        'pointerdown',
        (e) => {
          isDragging = false;
          isPointerDown = true;
          dragStartX = e.clientX;
          dragStartY = e.clientY;
        },
        { passive: true }
      );

      // Detect if movement exceeds threshold
      flickityEl.addEventListener(
        'pointermove',
        (e) => {
          if (!isPointerDown) return;
          const deltaX = Math.abs(e.clientX - dragStartX);
          const deltaY = Math.abs(e.clientY - dragStartY);
          if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
            isDragging = true;
          }
        },
        { passive: true }
      );

      // Reset on pointer up - use document level to catch releases outside carousel
      document.addEventListener(
        'pointerup',
        () => {
          if (isPointerDown) {
            resetDragState();
          }
        },
        { passive: true }
      );

      // Also reset on pointer cancel (e.g., touch interrupted)
      flickityEl.addEventListener('pointercancel', resetDragState, {
        passive: true,
      });

      // Intercept clicks on product links if dragging
      flickityEl.addEventListener(
        'click',
        (e) => {
          if (isDragging) {
            const link = e.target.closest(
              'a.rebuy-product-title-link, a.rebuy-product-image, a[href*="/products/"]'
            );
            if (link) {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        },
        true
      ); // Use capture phase to intercept before other handlers

      // Mark as patched
      flickityEl.dataset.dragFixPatched = 'true';
    });
  }

  /**
   * Reorder Flickity nav buttons to appear before carousel content in DOM
   * This fixes WCAG 2.4.3 Focus Order: nav buttons are visually above the carousel
   * but Flickity places them after the viewport in DOM, causing unintuitive tab order.
   * FOXH-166: Move Previous/Next buttons before the carousel items.
   */
  reorderNavButtonsForFocusOrder() {
    // Find all Flickity-enabled carousels
    const flickityCarousels = document.querySelectorAll('.flickity-enabled');

    flickityCarousels.forEach((carousel) => {
      // Skip if already reordered
      if (carousel.dataset.navReordered === 'true') return;

      const viewport = carousel.querySelector('.flickity-viewport');
      const prevButton = carousel.querySelector('.flickity-button.previous');
      const nextButton = carousel.querySelector('.flickity-button.next');

      // Only proceed if we have all required elements
      if (!viewport || !prevButton || !nextButton) return;

      // Create a nav container for buttons if it doesn't exist
      // This keeps buttons together and ensures proper DOM order
      let navContainer = carousel.querySelector('.flickity-nav-a11y');
      if (!navContainer) {
        navContainer = document.createElement('div');
        navContainer.className = 'flickity-nav-a11y';
        // Position container for screen reader/keyboard access but CSS handles visual position
        navContainer.setAttribute('aria-label', 'Carousel navigation');
        navContainer.style.cssText =
          'position: absolute; top: 0; left: 0; right: 0; z-index: 1; pointer-events: none;';
      }

      // Move buttons into the nav container (preserves event listeners)
      // Order: Previous then Next for natural left-to-right tab flow
      navContainer.appendChild(prevButton);
      navContainer.appendChild(nextButton);

      // Re-enable pointer events on buttons themselves
      prevButton.style.pointerEvents = 'auto';
      nextButton.style.pointerEvents = 'auto';

      // Insert nav container BEFORE the viewport so it comes first in tab order
      carousel.insertBefore(navContainer, viewport);

      // Mark as reordered to prevent duplicate processing
      carousel.dataset.navReordered = 'true';
    });
  }

  /**
   * Remove tabindex from non-interactive elements within Rebuy widgets
   * Fixes A11y issue: prices, titles, headings, and duplicate image links should not be keyboard-focusable
   * WCAG 2.1.1 (Keyboard) and 2.4.3 (Focus Order) compliance
   */
  removePriceTabindex() {
    // Find all Rebuy non-interactive elements that might have tabindex
    // Includes: prices, product titles (not the link inside), headings, and product image links
    const nonInteractiveElements = document.querySelectorAll(
      '.rebuy-product-price, .rebuy-money, .rebuy-product-block h1, .rebuy-product-block h2, .rebuy-product-block h3, .rebuy-product-block h4, .rebuy-product-block h5, .rebuy-product-block h6'
    );

    nonInteractiveElements.forEach((element) => {
      // Remove tabindex from the element itself if present
      if (element.hasAttribute('tabindex')) {
        element.removeAttribute('tabindex');
      }

      // Remove tabindex from any nested elements (Rebuy adds tabindex="0" to inner spans)
      // But preserve links (.rebuy-product-title-link) as they should remain focusable
      const nestedFocusable = element.querySelectorAll('[tabindex]:not(.rebuy-product-title-link)');
      nestedFocusable.forEach((nested) => {
        nested.removeAttribute('tabindex');
      });
    });

    // Remove tabindex from product image links - users can access product via title link
    // This reduces redundant tab stops
    const imageLinks = document.querySelectorAll('.rebuy-product-image[tabindex]');
    imageLinks.forEach((imageLink) => {
      imageLink.setAttribute('tabindex', '-1');
    });
  }

  /**
   * Handle hidden slides in Flickity carousels
   * Prevents keyboard access to off-screen slides by setting tabindex="-1"
   * and aria-hidden="true" on the slide container and focusable elements
   * WCAG 2.4.3 (Focus Order) compliance
   * FOXH-166 v2: Uses IntersectionObserver for reliable visibility detection
   *              instead of getBoundingClientRect() which has timing issues
   * @param {boolean} forceReobserve - Force re-setup of observers (use when Rebuy re-renders)
   */
  async handleHiddenSlides(forceReobserve = false) {
    // Find all Flickity-enabled carousels
    const flickityCarousels = document.querySelectorAll('.flickity-enabled');

    for (const carousel of flickityCarousels) {
      // Skip if already patched with v2, unless force re-observe is requested
      if (carousel.dataset.slideA11yPatched === 'v2' && !forceReobserve) continue;

      // Set up IntersectionObserver first (works without Flickity instance)
      this.setupVisibilityObserver(carousel);

      // Then try to hook into Flickity events for more responsive updates
      const flickityInstance = await this.waitForFlickity(carousel);

      if (flickityInstance) {
        // Listen to Flickity events for immediate feedback on slide changes
        // These use the IntersectionObserver's visibility Set for consistency
        flickityInstance.on('select', () => {
          // Small delay to let Flickity update positions
          requestAnimationFrame(() => {
            // Use observer's Set if available, otherwise fall back
            if (carousel._visibleSlides) {
              this.updateSlideAccessibilityWithVisibleSet(carousel, carousel._visibleSlides);
            }
          });
        });

        flickityInstance.on('settle', () => {
          // Use observer's Set if available, otherwise fall back
          if (carousel._visibleSlides) {
            this.updateSlideAccessibilityWithVisibleSet(carousel, carousel._visibleSlides);
          }
        });
      } else {
        // Fallback: listen for class changes on slides via MutationObserver
        this.observeSlideChanges(carousel);
      }

      // Mark as patched with v2 (IntersectionObserver approach)
      carousel.dataset.slideA11yPatched = 'v2';
    }
  }


  /**
   * Observe slide class changes for carousels without direct Flickity access
   * Uses the IntersectionObserver's visibility Set for consistent detection
   */
  observeSlideChanges(carousel) {
    const context = this;
    const slideObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          // Use observer's Set if available for consistent detection
          if (carousel._visibleSlides) {
            context.updateSlideAccessibilityWithVisibleSet(carousel, carousel._visibleSlides);
          }
        }
      });
    });

    const slides = carousel.querySelectorAll(
      '.flickity-slider > .rebuy-product-block, .flickity-slider > .flickity-cell'
    );

    slides.forEach((slide) => {
      slideObserver.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });
  }

  applyPromoPricing() {
    // Only apply if promo is enabled
    if (!window.showPromoOriginalPrice || !window.promoDiscountPercentage) {
      return;
    }

    // Find all Rebuy product blocks
    const productBlocks = document.querySelectorAll('.rebuy-product-block');

    productBlocks.forEach((block) => {
      const priceElement = block.querySelector('.rebuy-product-price');
      if (!priceElement) return;

      // Check if we've already processed this element
      if (priceElement.dataset.promoProcessed === 'true') return;

      // Check if this product is excluded from promo
      const productLink = block.querySelector('a[href*="/products/"]');
      if (productLink) {
        const productHandle = productLink
          .getAttribute('href')
          .split('/products/')[1]
          ?.split('?')[0];
        const isExcluded =
          window.promoExcludedProducts?.includes(productHandle) || false;
        if (isExcluded) {
          priceElement.dataset.promoProcessed = 'true';
          return;
        }
      }

      const moneyElement = priceElement.querySelector('.rebuy-money');
      if (!moneyElement) return;

      // Extract the current price
      const priceText = moneyElement.textContent.trim();
      const priceMatch = priceText.match(/\$?([\d,]+\.?\d*)/);
      if (!priceMatch) return;

      const currentPrice = parseFloat(priceMatch[1].replace(',', ''));

      // Check if there's already a compare-at price (don't override it)
      const hasComparePrice = priceElement.querySelector(
        '.rebuy-product-price-compare'
      );
      if (hasComparePrice) return;

      // Calculate sale price
      const discountMultiplier = (100 - window.promoDiscountPercentage) / 100;
      const salePrice = (currentPrice * discountMultiplier).toFixed(2);

      // Update the entire price display to be inline
      priceElement.innerHTML = `<span class="sr-only">${
        window.variantStrings?.salePrice || 'Sale price'
      }</span><span class="rebuy-money">$${salePrice}</span> <span class="sr-only">${
        window.variantStrings?.regularPrice || 'Regular price'
      }</span><s class="rebuy-money line-through opacity-50">$${currentPrice.toFixed(2)}</s>`;
      priceElement.style.whiteSpace = 'nowrap';

      // Mark as processed
      priceElement.dataset.promoProcessed = 'true';
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RebuyPromoPricing();
  });
} else {
  new RebuyPromoPricing();
}
